import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    const variant = event.detail.resource;

    // Selecting a variant must never move the viewport: the picker's own
    // finish name, availability line and ship date are the visible feedback
    // when the gallery is off-screen on mobile.
    if (!this.slideshow) {
      if (!source) return;
      const newMediaGallery = source.querySelector('media-gallery');
      if (newMediaGallery) this.replaceWith(newMediaGallery);
      return;
    }

    // Try featured_media first (set when variant images are linked in admin)
    const mediaId = variant?.featured_media?.id;
    if (mediaId != null) {
      const idx = this.#findSlideByMediaId(mediaId);
      if (idx >= 0) {
        this.slideshow.select(idx, undefined, { animate: false });
        return;
      }
    }

    // Fallback: match slide image filename to variant options.
    // Covers products whose variant images exist in the gallery but aren't
    // linked to their variants via featured_media (e.g. "square-walnut.jpg"
    // for option1="Square", option2="Walnut").
    const idx = this.#findSlideByOptions(variant);
    if (idx >= 0) {
      this.slideshow.select(idx, undefined, { animate: false });
      return;
    }

    // Last resort: replace gallery from fetched HTML
    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');
    if (newMediaGallery) this.replaceWith(newMediaGallery);
  };

  /**
   * Finds the slideshow-slide index whose product-media has the given media id.
   * @param {string|number} mediaId
   * @returns {number} slide index, or -1 if not found
   */
  #findSlideByMediaId(mediaId) {
    const idStr = String(mediaId);
    const slides = this.querySelectorAll('slideshow-slide');
    let found = -1;
    slides.forEach((slide, index) => {
      const marker = slide.querySelector('[data-media-id]');
      if (marker && String(marker.getAttribute('data-media-id')) === idStr) {
        found = index;
      }
    });
    return found;
  }

  /**
   * Finds a slide whose image src matches the variant's option values.
   * e.g. option1="Square" + option2="Walnut" → looks for "square-walnut" in src.
   * @param {Object} variant
   * @returns {number} slide index, or -1 if not found
   */
  #findSlideByOptions(variant) {
    const parts = [variant?.option1, variant?.option2, variant?.option3]
      .filter(Boolean)
      .map((v) => v.toLowerCase().replace(/\s+/g, ''));
    if (!parts.length) return -1;

    const pattern = parts.join('-');
    const slides = this.querySelectorAll('slideshow-slide');
    let found = -1;
    slides.forEach((slide, index) => {
      const img = slide.querySelector('img');
      if (img) {
        const src = (img.getAttribute('src') || '').toLowerCase();
        if (src.includes(pattern)) found = index;
      }
    });
    return found;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
